'use client';

import { useEffect, useState } from 'react';
import { useProxyStore } from '../store/proxy-store';
import {
  useProjectStore,
  type TrackingConfig as StoreTrackingConfig,
} from '../store/projectStore';
import { terraProxyApiClient } from '../clients/testerClient';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { getAuthHeaders } from '../utils/authHeaders';
import type {
  NetworkRequestSchema,
  PageTrackingSchema,
} from '@terra/shared-contracts-tracker';
import type { z } from 'zod';
import { shouldFlushBatchParametrizationOnBrowserClosed } from '../utils/batchParametrizationBrowserFlush';
import {
  requestBatchParametrizationOnBrowserClosed,
  stopTrackingSession,
} from '../utils/manualSession';
import { syncTorchTrackingBrowsersFromElectron } from '../utils/registerTorchTrackingBrowser';

const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;

type NetworkTrackingPayload = z.infer<typeof NetworkRequestSchema>;
type PageTrackingPayload = z.infer<typeof PageTrackingSchema>;

// Static handlers defined outside the hook to ensure stable references
const handleTrackingRequest = async (...args: unknown[]) => {
  const payload = args[0] as NetworkTrackingPayload;

  try {
    await terraProxyApiClient.track(payload);
  } catch (error) {
    console.error('[Terra] Failed to track network request:', error);
  }
};

const handleTrackingSeen = async (...args: unknown[]) => {
  const payload = args[0] as Parameters<
    typeof terraProxyApiClient.trackSeen
  >[0];

  try {
    await terraProxyApiClient.trackSeen(payload);
  } catch (error) {
    console.error('[Terra] Failed to record seen network request:', error);
  }
};

const handleTrackingScreenshot = async (...args: unknown[]) => {
  const payload = args[0] as PageTrackingPayload;

  try {
    await terraProxyApiClient.trackPages(payload);
  } catch (error) {
    console.error('[Terra] Failed to track page screenshot:', error);
  }
};

const handleUpdateWebpages = async (...args: unknown[]) => {
  const payload = args[0] as {
    applicationId: string;
    credentialId: string | null;
  };

  try {
    console.log('[Terra] Triggering update webpages:', payload);
    await terraProxyApiClient.triggerUpdateWebpages(
      payload.applicationId,
      payload.credentialId,
    );
    console.log('[Terra] Successfully triggered update webpages');
  } catch (error) {
    console.error('[Terra] Failed to trigger update webpages:', error);
  }
};

/**
 * Hook to manage Electron IPC listeners for tracking events.
 * Should be called exactly once in the application root (AppLayout).
 */
export function useTrackingListeners() {
  const isProxyRunning = useProxyStore((state) => state.isProxyRunning);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const { isTrackingEnabled, enableTracking, disableTracking } =
    useProjectStore();
  const trackingTargetId = useProjectStore((state) => state.trackingTargetId);

  const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

  // Check authentication status for Electron
  useEffect(() => {
    if (!isElectron) {
      setIsAuthenticated(true);
      setIsCheckingAuth(false);

      return;
    }

    if (window.electronAPI?.auth) {
      window.electronAPI.auth
        .isAuthenticated()
        .then((result) => {
          setIsAuthenticated(result.authenticated);
          setIsCheckingAuth(false);
        })
        .catch(() => {
          setIsAuthenticated(false);
          setIsCheckingAuth(false);
        });

      const cleanupSuccess = window.electronAPI.auth.onAuthSuccess(() => {
        setIsAuthenticated(true);
      });

      const cleanupLogout = window.electronAPI.session?.onLoggedOut(() => {
        setIsAuthenticated(false);
      });

      return () => {
        cleanupSuccess?.();
        cleanupLogout?.();
      };
    } else {
      // Auth API not yet available (initialization race condition)
      // Set isCheckingAuth to false to unblock, but not authenticated
      console.warn('[Terra] Electron auth API not available yet');
      setIsAuthenticated(false);
      setIsCheckingAuth(false);
    }
  }, [isElectron]);

  // Manage subscriptions
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI) {
      return;
    }

    const shouldAttach =
      isTrackingEnabled && isProxyRunning && isAuthenticated && !isCheckingAuth;

    if (!shouldAttach) {
      return;
    }

    console.log('[Terra] Attaching Electron tracking listeners');

    // Attach listeners
    window.electronAPI.on('tracking:network-request', handleTrackingRequest);
    window.electronAPI.on('tracking:network-seen', handleTrackingSeen);
    window.electronAPI.on('tracking:page-screenshot', handleTrackingScreenshot);
    window.electronAPI.on('update-webpages:trigger', handleUpdateWebpages);

    // Signal ready
    window.electronAPI
      .signalReady()
      .catch((err) => console.error('[Terra] Failed to signal ready:', err));

    // Cleanup function
    return () => {
      console.log('[Terra] Removing Electron tracking listeners');
      if (window.electronAPI.removeListener) {
        window.electronAPI.removeListener(
          'tracking:network-request',
          handleTrackingRequest,
        );
        window.electronAPI.removeListener(
          'tracking:network-seen',
          handleTrackingSeen,
        );
        window.electronAPI.removeListener(
          'tracking:page-screenshot',
          handleTrackingScreenshot,
        );
        window.electronAPI.removeListener(
          'update-webpages:trigger',
          handleUpdateWebpages,
        );
      }
    };
  }, [isTrackingEnabled, isProxyRunning, isAuthenticated, isCheckingAuth]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.browser) {
      return;
    }

    const shouldListen =
      isTrackingEnabled &&
      isAuthenticated &&
      !isCheckingAuth &&
      !!trackingTargetId;

    if (!shouldListen) {
      return;
    }

    const unsubscribe = window.electronAPI.browser.onInstanceClosed((data) => {
      const {
        trackingBrowserIds,
        trackingTargetId: applicationId,
        unregisterTrackingBrowserId,
      } = useProjectStore.getState();

      if (
        !shouldFlushBatchParametrizationOnBrowserClosed({
          closedBrowserId: data.browserId,
          trackingBrowserIds,
        })
      ) {
        console.log(
          '[Terra] Ignoring browser close for batch parametrization flush',
          { browserId: data.browserId, trackingBrowserIds },
        );

        return;
      }

      console.log(
        '[Terra] Requesting batch parametrization flush for TORCH tracking browser close',
        { browserId: data.browserId, applicationId },
      );
      unregisterTrackingBrowserId(data.browserId);
      void requestBatchParametrizationOnBrowserClosed(applicationId);
    });

    return () => {
      unsubscribe?.();
    };
  }, [isTrackingEnabled, isAuthenticated, isCheckingAuth, trackingTargetId]);

  // Sync tracking state...
  useEffect(() => {
    let mounted = true;

    const syncTrackingState = async () => {
      if (typeof window === 'undefined' || !window.electronAPI) {
        return;
      }

      // Only sync tracking state when authenticated to prevent UI/state mismatch
      if (!isAuthenticated || isCheckingAuth) {
        return;
      }

      try {
        const activeResult = await window.electronAPI.tracking.isActive();

        if (!mounted || !activeResult.success) {
          return;
        }

        if (activeResult.isActive === true && !isTrackingEnabled) {
          const sessionResult = await window.electronAPI.tracking.getSession();

          if (mounted && sessionResult.success && sessionResult.session) {
            const { config, credentialId, crawlerSessionId, applicationId } =
              sessionResult.session;

            const currentTargetId = useProjectStore.getState().targetId;

            if (applicationId && applicationId !== currentTargetId) {
              console.log(
                '[Terra] Syncing: Electron session belongs to a different app, stopping orphaned session',
              );

              try {
                await window.electronAPI.tracking.stop();
              } catch (e) {
                console.warn(
                  '[Terra] Failed to stop orphaned Electron session',
                  e,
                );
              }

              return;
            }

            const storeConfig: StoreTrackingConfig = {
              recordNetworkTraffic:
                config.trackingConfig?.recordNetworkTraffic ??
                config.recordNetworkTraffic ??
                true,
              recordPageScreenshots:
                config.trackingConfig?.recordPageScreenshots ??
                config.recordPageScreenshots ??
                true,
              responseMimeTypes:
                config.trackingConfig?.responseMimeTypes ??
                config.responseMimeTypes,
              skipNoiseUrlExtensions:
                config.trackingConfig?.skipNoiseUrlExtensions ??
                config.skipNoiseUrlExtensions,
            };

            console.log(
              '[Terra] Syncing tracking state: Enabling tracking in UI',
            );
            enableTracking(
              storeConfig,
              credentialId,
              crawlerSessionId,
              applicationId,
            );
            void syncTorchTrackingBrowsersFromElectron({
              trackingTargetId: applicationId,
            });
          }
        } else if (activeResult.isActive === true && isTrackingEnabled) {
          const { trackingTargetId: applicationId } =
            useProjectStore.getState();

          void syncTorchTrackingBrowsersFromElectron({
            trackingTargetId: applicationId,
          });
        } else if (activeResult.isActive === false && isTrackingEnabled) {
          console.log(
            '[Terra] Syncing tracking state: Disabling tracking in UI',
          );
          disableTracking();
        }
      } catch (error) {
        console.error('[Terra] Failed to sync tracking state:', error);
      }
    };

    if (isProxyRunning) {
      syncTrackingState();
    } else if (isTrackingEnabled) {
      const {
        trackingTargetId: applicationId,
        trackingCrawlerSessionId: sessionId,
      } = useProjectStore.getState();

      void stopTrackingSession(applicationId, sessionId);
      disableTracking();
    }

    return () => {
      mounted = false;
    };
  }, [
    isProxyRunning,
    isTrackingEnabled,
    isAuthenticated,
    isCheckingAuth,
    enableTracking,
    disableTracking,
  ]);

  useEffect(() => {
    if (!isTrackingEnabled) {
      return;
    }

    const sendHeartbeat = async () => {
      const {
        trackingTargetId: appId,
        trackingCrawlerSessionId: sessionId,
        isTrackingEnabled: stillEnabled,
      } = useProjectStore.getState();

      if (!stillEnabled || !appId || !sessionId) return;

      try {
        const authHeaders = await getAuthHeaders();

        await applicationsClient.heartbeatManualCrawlerSession({
          params: { id: appId, sessionId },
          body: {},
          overrideClientOptions: {
            baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
          },
          extraHeaders: { ...authHeaders },
        });
      } catch (err) {
        console.warn('Manual session heartbeat failed:', err);
      }
    };

    const intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [isTrackingEnabled]);
}
